import { useEffect, useRef } from "react";

// Store the truly original methods at module level to prevent nested overrides
const ORIGINAL_HISTORY_METHODS = {
  back: window.history.back,
  forward: window.history.forward,
  go: window.history.go,
};

export function useUnsavedChangesBlocker(
  shouldBlock,
  message = "You have unsaved changes. Are you sure you want to leave?",
  onConfirmNavigate
) {
  const isBlockingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const handlerRef = useRef(null);
  const onConfirmRef = useRef(onConfirmNavigate);
  onConfirmRef.current = onConfirmNavigate;

  useEffect(() => {
    // Mark as initialized after first render
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
    }
    
    // If shouldBlock is false, ALWAYS disable and restore
    if (!shouldBlock) {
      isBlockingRef.current = false;
      
      // CRITICAL: Always restore truly original methods when disabling
      // This ensures we don't keep overridden methods from previous components
      window.history.back = ORIGINAL_HISTORY_METHODS.back;
      window.history.forward = ORIGINAL_HISTORY_METHODS.forward;
      window.history.go = ORIGINAL_HISTORY_METHODS.go;
      
      // Remove beforeunload listener if it exists
      if (handlerRef.current) {
        window.removeEventListener("beforeunload", handlerRef.current);
        handlerRef.current = null;
      }
      
      return;
    }

    // Only set up blocker if we should block AND we're initialized
    if (!isInitializedRef.current) {
      return;
    }

    isBlockingRef.current = true;

    // Override window.history.back to handle it properly
    window.history.back = function() {
      if (isBlockingRef.current) {
        const confirmed = window.confirm(message);
        if (!confirmed) {
          return;
        }
        // Disable blocking before navigating so the browser's
        // native beforeunload dialog doesn't appear as a second prompt.
        isBlockingRef.current = false;
        // When callback is provided, it handles navigation (e.g. history.back + reload).
        // We must NOT call ORIGINAL_HISTORY_METHODS.back here, or we'd navigate twice.
        if (onConfirmRef.current) {
          onConfirmRef.current();
          return;
        }
      }
      return ORIGINAL_HISTORY_METHODS.back.call(this);
    };

    // Override window.history.forward to handle it properly
    window.history.forward = function() {
      if (isBlockingRef.current) {
        const confirmed = window.confirm(message);
        if (!confirmed) {
          return;
        }
        isBlockingRef.current = false;
      }
      return ORIGINAL_HISTORY_METHODS.forward.call(this);
    };

    // Override window.history.go to handle it properly
    window.history.go = function(delta) {
      if (isBlockingRef.current) {
        const confirmed = window.confirm(message);
        if (!confirmed) {
          return;
        }
        isBlockingRef.current = false;
      }
      return ORIGINAL_HISTORY_METHODS.go.call(this, delta);
    };

    // Set up browser event for tab close/refresh
    const handleBeforeUnload = (e) => {
      if (isBlockingRef.current) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    handlerRef.current = handleBeforeUnload;

    // Add event listener for beforeunload
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function
    return () => {
      // Restore truly original window.history methods
      window.history.back = ORIGINAL_HISTORY_METHODS.back;
      window.history.forward = ORIGINAL_HISTORY_METHODS.forward;
      window.history.go = ORIGINAL_HISTORY_METHODS.go;

      // Remove event listener
      if (handlerRef.current) {
        window.removeEventListener("beforeunload", handlerRef.current);
        handlerRef.current = null;
      }
      
      isBlockingRef.current = false;
    };
  }, [shouldBlock, message, onConfirmNavigate]);
}
